import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import * as Core from 'app/core/models/essette-api';
import { apiCall } from 'app/shared/lib/essette-api';
import { CustomFieldService } from 'app/core/services/custom-field-service';
import { Toaster } from 'app/shared/lib/toaster';
import { compareObjects } from 'app/shared/lib/compare-objects';
import { getReference } from 'app/shared/models/essette-types';
import { I18N } from 'aurelia-i18n';

@autoinject()
export class SaveCustomFieldChoice extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected choice: Core.CustomFieldChoice = {};

    @bindable.object({ defaultBindingMode: bindingMode.toView })
    protected definition: Core.CustomFieldDefinition = {};

    // when false, Save button is disabled
    public choiceChanged = false;

    // protected editedChoice: Core.CustomFieldChoice | null = null;
    @observable
    protected code: string = '';
    @observable
    protected description: string = '';

    protected maxCodeTextLength: number = 50;
    protected maxDescriptionTextLength: number = 100;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly customFieldService: CustomFieldService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected reload(): void {
        this.code = this.choice.code ?? '';
        this.description = this.choice.description ?? '';

    }

    protected constructSaveObject(): Core.CustomFieldChoice {
        const saveChoice: Core.CustomFieldChoice = {
            id: this.choice?.id,
            description: this.description,
            code: this.code,
            definition: getReference(this.definition)
        }

        return saveChoice;
    }

    protected codeChanged(): void {
        this.hasChanges();
    }

    protected descriptionChanged(): void {
        this.hasChanges();
    }

    ////****************** Functions ************************************************////


    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        const originalChoice: Core.CustomFieldChoice = {
            code: this.choice.code,
            description: this.choice.description
        }

        const editedChoice: Core.CustomFieldChoice = {
            code: this.code,
            description: this.description
        }

        const result = compareObjects(originalChoice, editedChoice, { returnDifferences: true });
        return this.choiceChanged = !result.areEqual;
    }

    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        return await this.saveChoice();
    }

    public reset(): void {
        this.reload();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveChoice(): Promise<boolean> {
        if(!this.definition || !this.definition.id) {
            return false;
        }

        const saveChoice: Core.CustomFieldChoice = this.constructSaveObject();

        let isSuccessful = false;

        try {
            this.state.beginSave();

            const response = await this.customFieldService.saveCustomFieldChoice(saveChoice);
            isSuccessful = response.isSuccessful;
            this.toaster.standard.saved('core:userFieldReference.lookup.$', isSuccessful);
        } catch(error) {
            // if duplicate constraint violated, show toast
            if(error.status.errors) {
                const invalidCode = error.status.errors.some((err: any) =>
                    err.code === 'property.constraintViolated' && (err.description === 'Duplicate UserReferenceCode value exists' || err.description === 'A User Reference Choice with the same User Field Target and Code already exists.'));
                if(invalidCode) {
                    this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.message', { what: this.code })).show();
                } else {
                    this.toaster.standard.saved('core:userFieldReference.lookup.$', false);
                }
            }
            else {
                this.toaster.standard.saved('core:userFieldReference.lookup.$', false);
            }
            throw error;
        } finally {
            this.state.endSave();
            return isSuccessful;
        }
    }


}
